import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminAPI, ordersAPI } from "../../utils/api";
import {
  FileText,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Search,
  Filter,
  Plus,
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X,
  Trash2,
  Package,
  MapPin,
  Truck,
  Send,
} from "lucide-react";
import toast from "react-hot-toast";

const statusConfig = {
  draft: {
    color: "bg-gray-100 text-gray-800 border-gray-200",
    label: "Draft",
    icon: FileText,
  },
  sent: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    label: "Sent",
    icon: Send,
  },
  paid: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    label: "Paid",
    icon: CheckCircle,
  },
  partial: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    label: "Partial",
    icon: DollarSign,
  },
  overdue: {
    color: "bg-rose-100 text-rose-800 border-rose-200",
    label: "Overdue",
    icon: AlertCircle,
  },
  cancelled: {
    color: "bg-slate-100 text-slate-800 border-slate-200",
    label: "Cancelled",
    icon: XCircle,
  },
};

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div
    className={`card p-5 border-l-4 ${color.replace("bg-", "border-").replace("100", "500")}`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

const AdminInvoices = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState("from_order");
  const [selectedOrderId, setSelectedOrderId] = useState("");

  const [createForm, setCreateForm] = useState({
    user_id: "",
    items: [{ description: "", quantity: 1, unit_price: "" }],
    tax_rate: 14,
    discount_amount: 0,
    due_date: "",
    notes: "",
    terms: "",
  });

  const queryClient = useQueryClient();

  const {
    data: invoicesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminInvoices", page, search, statusFilter],
    queryFn: () =>
      adminAPI.invoices({
        page,
        per_page: 10,
        search: search || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      }),
    keepPreviousData: true,
  });

  const { data: statsData } = useQuery({
    queryKey: ["invoiceStats"],
    queryFn: () => adminAPI.invoiceStats(),
  });

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ["deliveredOrdersForInvoice"],
    queryFn: () =>
      ordersAPI.list({
        status: "delivered",
        per_page: 50,
      }),
    enabled: showCreateModal && createMode === "from_order",
  });

  const deliveredOrders = ordersData?.data?.data?.data || [];

  const createFromOrderMutation = useMutation({
    mutationFn: (orderId) => adminAPI.createInvoiceFromOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminInvoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceStats"] });
      toast.success("Invoice created from order successfully");
      setShowCreateModal(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create invoice from order",
      );
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => adminAPI.createInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminInvoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceStats"] });
      toast.success("Invoice created successfully");
      setShowCreateModal(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create invoice");
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, paid_amount, payment_method }) =>
      adminAPI.updateInvoiceStatus(id, { status, paid_amount, payment_method }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminInvoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceStats"] });
      toast.success("Invoice status updated");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update status");
    },
  });

  const generatePdfMutation = useMutation({
    mutationFn: (id) => adminAPI.generateInvoicePdf(id),
    onSuccess: (res) => {
      window.open(res.data.data.url, "_blank");
      toast.success("PDF generated");
    },
    onError: () => toast.error("Failed to generate PDF"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminAPI.deleteInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminInvoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceStats"] });
      toast.success("Invoice deleted");
    },
    onError: () => toast.error("Failed to delete invoice"),
  });

  const invoices = invoicesData?.data?.data?.data || [];
  const stats = invoicesData?.data?.stats || {};
  const pagination = invoicesData?.data?.data || {};
  const dashboardStats = statsData?.data?.data || {};

  const totalPages = pagination.last_page || 1;

  const resetForm = () => {
    setCreateForm({
      user_id: "",
      items: [{ description: "", quantity: 1, unit_price: "" }],
      tax_rate: 14,
      discount_amount: 0,
      due_date: "",
      notes: "",
      terms: "",
    });
    setSelectedOrderId("");
    setCreateMode("from_order");
  };

  const handleStatusChange = (id, newStatus) => {
    const payload = { status: newStatus };
    if (newStatus === "paid") {
      payload.payment_method = "cash";
    }
    updateStatusMutation.mutate({ id, ...payload });
  };

  const handleCreateFromOrder = () => {
    if (!selectedOrderId) {
      toast.error("Please select an order");
      return;
    }
    createFromOrderMutation.mutate(selectedOrderId);
  };

  const handleCreateManual = (e) => {
    e.preventDefault();
    if (!createForm.user_id) {
      toast.error("Please enter a customer ID");
      return;
    }
    if (!createForm.due_date) {
      toast.error("Please select a due date");
      return;
    }

    const validItems = createForm.items.filter(
      (item) => item.description && item.unit_price && item.quantity > 0,
    );

    if (validItems.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    createMutation.mutate({
      ...createForm,
      items: validItems.map((item) => ({
        ...item,
        unit_price: parseFloat(item.unit_price),
        quantity: parseInt(item.quantity),
      })),
      due_date: createForm.due_date,
    });
  };

  const addItem = () => {
    setCreateForm({
      ...createForm,
      items: [
        ...createForm.items,
        { description: "", quantity: 1, unit_price: "" },
      ],
    });
  };

  const removeItem = (index) => {
    if (createForm.items.length === 1) {
      toast.error("At least one item is required");
      return;
    }
    const newItems = createForm.items.filter((_, i) => i !== index);
    setCreateForm({ ...createForm, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...createForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setCreateForm({ ...createForm, items: newItems });
  };

  const formatMoney = (val) => {
    const num =
      typeof val === "string"
        ? parseFloat(val)
        : typeof val === "number"
          ? val
          : 0;
    return isNaN(num)
      ? "0.00"
      : num.toLocaleString("en-US", { minimumFractionDigits: 2 });
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const isOverdue = (invoice) => {
    return (
      invoice.status !== "paid" &&
      invoice.due_date &&
      new Date(invoice.due_date) < new Date()
    );
  };

  const calculateTotals = useMemo(() => {
    const subtotal = createForm.items.reduce((sum, item) => {
      const price = parseFloat(item.unit_price) || 0;
      const qty = parseInt(item.quantity) || 0;
      return sum + price * qty;
    }, 0);
    const tax = (subtotal * (parseFloat(createForm.tax_rate) || 0)) / 100;
    const discount = parseFloat(createForm.discount_amount) || 0;
    const total = subtotal + tax - discount;
    return { subtotal, tax, discount, total };
  }, [createForm]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <p className="font-medium">Failed to load invoices</p>
        <p className="text-sm mt-1">{error.message}</p>
        <button
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["adminInvoices"] })
          }
          className="mt-3 btn-secondary text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600 mt-1">Manage billing and payments</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Invoice
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Outstanding"
          value={`EGP ${formatMoney(stats.total_outstanding)}`}
          icon={DollarSign}
          color="bg-amber-100 text-amber-600"
          subtitle={`${stats.sent_count || 0} unpaid invoices`}
        />
        <StatCard
          title="Total Paid"
          value={`EGP ${formatMoney(stats.total_paid)}`}
          icon={CheckCircle}
          color="bg-emerald-100 text-emerald-600"
          subtitle={`${stats.paid_count || 0} paid invoices`}
        />
        <StatCard
          title="Overdue"
          value={`EGP ${formatMoney(stats.total_overdue)}`}
          icon={AlertCircle}
          color="bg-rose-100 text-rose-600"
          subtitle={`${stats.overdue_count || 0} overdue invoices`}
        />
        <StatCard
          title="This Month"
          value={`EGP ${formatMoney(dashboardStats.this_month?.total || 0)}`}
          icon={Calendar}
          color="bg-blue-100 text-blue-600"
          subtitle={`${dashboardStats.this_month?.count || 0} invoices`}
        />
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by invoice # or customer name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field md:w-48"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Invoice #
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Customer
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Due Date
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">
                  Amount
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                  Status
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No invoices found</p>
                    <p className="text-sm mt-1">Create your first invoice</p>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => {
                  const StatusIcon =
                    statusConfig[invoice.status]?.icon || FileText;
                  const isInvoiceOverdue = isOverdue(invoice);
                  return (
                    <tr
                      key={invoice.id}
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        isInvoiceOverdue ? "bg-rose-50/50" : ""
                      }`}
                    >
                      <td className="py-3 px-4">
                        <span className="font-mono font-semibold text-gray-900">
                          {invoice.invoice_number}
                        </span>
                        {invoice.order && (
                          <p className="text-xs text-gray-400">
                            Order: {invoice.order.awb}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-gray-900">
                          {invoice.user?.name || "N/A"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {invoice.user?.email}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDate(invoice.issue_date)}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`text-sm ${isInvoiceOverdue ? "text-rose-600 font-medium" : "text-gray-600"}`}
                        >
                          {formatDate(invoice.due_date)}
                          {isInvoiceOverdue && (
                            <span className="ml-1 text-xs">(Overdue)</span>
                          )}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <p className="text-sm font-semibold text-gray-900">
                          EGP {formatMoney(invoice.total_amount)}
                        </p>
                        {invoice.balance_due > 0 &&
                          invoice.status !== "paid" && (
                            <p className="text-xs text-amber-600">
                              Due: EGP {formatMoney(invoice.balance_due)}
                            </p>
                          )}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                            isInvoiceOverdue
                              ? "bg-rose-100 text-rose-800 border-rose-200"
                              : statusConfig[invoice.status]?.color
                          }`}
                        >
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {isInvoiceOverdue
                            ? "Overdue"
                            : statusConfig[invoice.status]?.label ||
                              invoice.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setShowDetailModal(true);
                            }}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-lg hover:bg-blue-50"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              generatePdfMutation.mutate(invoice.id)
                            }
                            disabled={generatePdfMutation.isPending}
                            className="p-2 text-gray-400 hover:text-emerald-600 transition-colors rounded-lg hover:bg-emerald-50"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {/* FIXED: Status action buttons */}
                          {invoice.status === "draft" && (
                            <button
                              onClick={() =>
                                handleStatusChange(invoice.id, "sent")
                              }
                              disabled={updateStatusMutation.isPending}
                              className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-lg hover:bg-blue-50"
                              title="Mark as Sent"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}
                          {invoice.status !== "paid" &&
                            invoice.status !== "cancelled" && (
                              <button
                                onClick={() =>
                                  handleStatusChange(invoice.id, "paid")
                                }
                                disabled={updateStatusMutation.isPending}
                                className="p-2 text-gray-400 hover:text-emerald-600 transition-colors rounded-lg hover:bg-emerald-50"
                                title="Mark as Paid"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                          <button
                            onClick={() => {
                              if (window.confirm("Delete this invoice?"))
                                deleteMutation.mutate(invoice.id);
                            }}
                            className="p-2 text-gray-400 hover:text-rose-600 transition-colors rounded-lg hover:bg-rose-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">
                Create Invoice
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Mode Toggle */}
            <div className="px-6 pt-4">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setCreateMode("from_order")}
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${createMode === "from_order" ? "bg-white text-primary-600 shadow-sm" : "text-gray-500"}`}
                >
                  From Order
                </button>
                <button
                  onClick={() => setCreateMode("manual")}
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${createMode === "manual" ? "bg-white text-primary-600 shadow-sm" : "text-gray-500"}`}
                >
                  Manual
                </button>
              </div>
            </div>

            {/* From Order Mode */}
            {createMode === "from_order" && (
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Delivered Order{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  {ordersLoading ? (
                    <div className="flex items-center py-4 text-gray-400">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Loading orders...
                    </div>
                  ) : deliveredOrders.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 bg-gray-50 rounded-lg">
                      <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p>No delivered orders available</p>
                      <p className="text-xs mt-1">
                        Orders must be delivered before invoicing
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {deliveredOrders.map((order) => (
                        <div
                          key={order.id}
                          onClick={() => setSelectedOrderId(order.id)}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedOrderId === order.id
                              ? "border-primary-500 bg-primary-50"
                              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <Truck className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-mono text-sm font-semibold text-gray-900">
                                  {order.tracking_number}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {order.customer?.name} •{" "}
                                  {order.delivery_address?.substring(0, 30)}...
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">
                                EGP {formatMoney(order.total_amount)}
                              </p>
                              <p className="text-xs text-gray-400">
                                {formatDate(order.delivered_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedOrderId && (
                  <div className="p-4 bg-primary-50 rounded-xl border border-primary-200">
                    <p className="text-sm font-medium text-primary-900 mb-2">
                      Selected Order
                    </p>
                    {(() => {
                      const order = deliveredOrders.find(
                        (o) => o.id === selectedOrderId,
                      );
                      if (!order) return null;
                      return (
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Tracking:</span>
                            <span className="font-medium">
                              {order.tracking_number}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Customer:</span>
                            <span className="font-medium">
                              {order.customer?.name}
                            </span>
                          </div>
                          <div className="flex justify-between text-base font-bold pt-2 border-t border-primary-200">
                            <span className="text-primary-900">
                              Total Amount:
                            </span>
                            <span className="text-primary-600">
                              EGP {formatMoney(order.total_amount)}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateFromOrder}
                    disabled={
                      !selectedOrderId || createFromOrderMutation.isPending
                    }
                    className="btn-primary disabled:opacity-50 flex items-center"
                  >
                    {createFromOrderMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Create Invoice
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Manual Mode */}
            {createMode === "manual" && (
              <form onSubmit={handleCreateManual} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={createForm.user_id}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, user_id: e.target.value })
                    }
                    placeholder="Enter customer user ID"
                    className="input-field w-full"
                    required
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Items <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={addItem}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      + Add Item
                    </button>
                  </div>
                  <div className="space-y-3">
                    {createForm.items.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) =>
                              updateItem(index, "description", e.target.value)
                            }
                            placeholder="Description"
                            className="input-field w-full text-sm"
                            required
                          />
                        </div>
                        <div className="w-20">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) =>
                              updateItem(index, "quantity", e.target.value)
                            }
                            placeholder="Qty"
                            min="1"
                            className="input-field w-full text-sm"
                            required
                          />
                        </div>
                        <div className="w-28">
                          <input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) =>
                              updateItem(index, "unit_price", e.target.value)
                            }
                            placeholder="Price"
                            min="0"
                            step="0.01"
                            className="input-field w-full text-sm"
                            required
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="p-2 text-gray-400 hover:text-rose-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax Rate (%)
                    </label>
                    <input
                      type="number"
                      value={createForm.tax_rate}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          tax_rate: e.target.value,
                        })
                      }
                      min="0"
                      max="100"
                      className="input-field w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Amount (EGP)
                    </label>
                    <input
                      type="number"
                      value={createForm.discount_amount}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          discount_amount: e.target.value,
                        })
                      }
                      min="0"
                      step="0.01"
                      className="input-field w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={createForm.due_date}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, due_date: e.target.value })
                    }
                    className="input-field w-full"
                    required
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">
                      EGP {formatMoney(calculateTotals.subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">
                      Tax ({createForm.tax_rate}%):
                    </span>
                    <span className="font-medium">
                      EGP {formatMoney(calculateTotals.tax)}
                    </span>
                  </div>
                  {calculateTotals.discount > 0 && (
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Discount:</span>
                      <span className="font-medium text-rose-600">
                        -EGP {formatMoney(calculateTotals.discount)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                    <span>Total:</span>
                    <span>EGP {formatMoney(calculateTotals.total)}</span>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="btn-primary disabled:opacity-50 flex items-center"
                  >
                    {createMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Invoice
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Invoice {selectedInvoice.invoice_number}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {formatDate(selectedInvoice.issue_date)} - Due:{" "}
                  {formatDate(selectedInvoice.due_date)}
                </p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs font-medium text-gray-500 uppercase">
                    Customer
                  </p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">
                    {selectedInvoice.user?.name || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedInvoice.user?.email}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedInvoice.user?.phone}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs font-medium text-gray-500 uppercase">
                    Status
                  </p>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-2 ${statusConfig[selectedInvoice.status]?.color}`}
                  >
                    {statusConfig[selectedInvoice.status]?.label ||
                      selectedInvoice.status}
                  </span>
                  {selectedInvoice.payment_method && (
                    <p className="text-sm text-gray-600 mt-2">
                      Paid via: {selectedInvoice.payment_method}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <p className="text-xs text-blue-600 font-medium">Subtotal</p>
                  <p className="text-lg font-bold text-blue-900">
                    EGP {formatMoney(selectedInvoice.subtotal)}
                  </p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <p className="text-xs text-purple-600 font-medium">Tax</p>
                  <p className="text-lg font-bold text-purple-900">
                    EGP {formatMoney(selectedInvoice.tax_amount)}
                  </p>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-xl">
                  <p className="text-xs text-emerald-600 font-medium">Total</p>
                  <p className="text-lg font-bold text-emerald-900">
                    EGP {formatMoney(selectedInvoice.total_amount)}
                  </p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-xl">
                  <p className="text-xs text-amber-600 font-medium">
                    Balance Due
                  </p>
                  <p className="text-lg font-bold text-amber-900">
                    EGP {formatMoney(selectedInvoice.balance_due)}
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                {selectedInvoice.status === "draft" && (
                  <button
                    onClick={() => {
                      handleStatusChange(selectedInvoice.id, "sent");
                      setShowDetailModal(false);
                    }}
                    disabled={updateStatusMutation.isPending}
                    className="flex-1 btn-primary py-2.5 disabled:opacity-50 flex items-center justify-center"
                  >
                    <Send className="w-4 h-4 mr-2" /> Mark as Sent
                  </button>
                )}
                {selectedInvoice.status !== "paid" &&
                  selectedInvoice.status !== "cancelled" && (
                    <button
                      onClick={() => {
                        handleStatusChange(selectedInvoice.id, "paid");
                        setShowDetailModal(false);
                      }}
                      disabled={updateStatusMutation.isPending}
                      className="flex-1 btn-primary py-2.5 disabled:opacity-50 flex items-center justify-center"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Mark as Paid
                    </button>
                  )}
                <button
                  onClick={() => generatePdfMutation.mutate(selectedInvoice.id)}
                  disabled={generatePdfMutation.isPending}
                  className="flex-1 btn-secondary py-2.5 disabled:opacity-50 flex items-center justify-center"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvoices;
